import React, { useState } from 'react';
import type { SurveyComponent, SurveySection, SurveyQuestion } from '../../../types/builder';
import { Star, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

interface SurveyRendererProps {
  component: SurveyComponent;
}

export const SurveyRenderer: React.FC<SurveyRendererProps> = ({ component }) => {
  const { settings } = component;
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [submitted, setSubmitted] = useState(false);

  // Get sections (either from new format or legacy format)
  const sections: SurveySection[] = settings.enableSections && settings.sections
    ? settings.sections
    : [{
        id: 'default',
        title: '',
        description: '',
        questions: settings.questions || [],
      }];

  const currentSection = sections[currentSectionIndex];
  const totalSections = sections.length;
  const isLastSection = currentSectionIndex === totalSections - 1;
  const isFirstSection = currentSectionIndex === 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check required fields in current section
    const requiredQuestions = currentSection.questions.filter(q => q.required);
    const missingRequired = requiredQuestions.some(q => !responses[q.id]);
    
    if (missingRequired) {
      alert('Please answer all required questions');
      return;
    }

    if (isLastSection) {
      setSubmitted(true);
    } else {
      setCurrentSectionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstSection) {
      setCurrentSectionIndex(prev => prev - 1);
    }
  };

  const handleResponseChange = (questionId: string, value: any) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const handleCheckboxChange = (questionId: string, option: string, checked: boolean) => {
    setResponses(prev => {
      const current = prev[questionId] || [];
      if (checked) {
        return { ...prev, [questionId]: [...current, option] };
      } else {
        return { ...prev, [questionId]: current.filter((v: string) => v !== option) };
      }
    });
  };

  const renderQuestion = (question: SurveyQuestion, index: number) => (
    <div key={question.id} className="bg-white rounded-xl shadow-md p-6 mb-4">
      <div className="flex items-start gap-2 mb-4">
        <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
          {index + 1}
        </span>
        <div className="flex-1">
          <label className="block text-lg font-semibold text-gray-900">
            {question.question}
            {question.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {question.description && (
            <p className="text-sm text-gray-600 mt-1">{question.description}</p>
          )}
          {question.validation?.type && question.validation.type !== 'none' && (
            <p className="text-xs text-blue-600 mt-1">
              ⚡ {
                question.validation.type === 'email' ? 'Email format required' :
                question.validation.type === 'phone' ? 'Phone number format required' :
                question.validation.type === 'number' ? `Number${question.validation.min ? ` (min: ${question.validation.min})` : ''}${question.validation.max ? ` (max: ${question.validation.max})` : ''}` :
                question.validation.type === 'url' ? 'URL format required' : ''
              }
            </p>
          )}
        </div>
      </div>

      <div className="ml-10">
        {/* Multiple Choice */}
        {question.type === 'multipleChoice' && (
          <div className="space-y-2">
            {question.options?.map((option, optionIndex) => (
              <label
                key={optionIndex}
                className="flex items-center gap-3 p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all"
              >
                <input
                  type="radio"
                  name={question.id}
                  value={option}
                  checked={responses[question.id] === option}
                  onChange={(e) => handleResponseChange(question.id, e.target.value)}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-gray-900">{option}</span>
              </label>
            ))}
          </div>
        )}

        {/* Checkbox */}
        {question.type === 'checkbox' && (
          <div className="space-y-2">
            {question.options?.map((option, optionIndex) => (
              <label
                key={optionIndex}
                className="flex items-center gap-3 p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all"
              >
                <input
                  type="checkbox"
                  checked={(responses[question.id] || []).includes(option)}
                  onChange={(e) => handleCheckboxChange(question.id, option, e.target.checked)}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-gray-900">{option}</span>
              </label>
            ))}
          </div>
        )}

        {/* Dropdown */}
        {question.type === 'dropdown' && (
          <select
            value={responses[question.id] || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
          >
            <option value="">-- Select an option --</option>
            {question.options?.map((option, optionIndex) => (
              <option key={optionIndex} value={option}>
                {option}
              </option>
            ))}
          </select>
        )}

        {/* Short Text */}
        {question.type === 'text' && (
          <input
            type="text"
            value={responses[question.id] || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Your answer..."
          />
        )}

        {/* Long Text */}
        {question.type === 'textarea' && (
          <textarea
            value={responses[question.id] || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={4}
            placeholder="Your answer..."
          />
        )}

        {/* Date */}
        {question.type === 'date' && (
          <input
            type="date"
            value={responses[question.id] || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
          />
        )}

        {/* Rating */}
        {question.type === 'rating' && (
          <div className="flex items-center gap-2">
            {Array.from({ length: question.maxRating || 5 }, (_, i) => i + 1).map((rating) => (
              <button
                key={rating}
                type="button"
                onClick={() => handleResponseChange(question.id, rating)}
                className="transition-transform hover:scale-110"
              >
                <Star
                  size={36}
                  fill={responses[question.id] >= rating ? '#fbbf24' : 'none'}
                  stroke={responses[question.id] >= rating ? '#fbbf24' : '#d1d5db'}
                  className="transition-colors"
                />
              </button>
            ))}
            {responses[question.id] && (
              <span className="ml-3 text-lg font-semibold text-gray-900">
                {responses[question.id]}/{question.maxRating}
              </span>
            )}
          </div>
        )}

        {/* Yes/No */}
        {question.type === 'yesNo' && (
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => handleResponseChange(question.id, 'Yes')}
              className={`flex-1 py-3 px-6 rounded-xl font-semibold transition-all ${
                responses[question.id] === 'Yes'
                  ? 'bg-green-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              👍 Yes
            </button>
            <button
              type="button"
              onClick={() => handleResponseChange(question.id, 'No')}
              className={`flex-1 py-3 px-6 rounded-xl font-semibold transition-all ${
                responses[question.id] === 'No'
                  ? 'bg-red-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              👎 No
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (submitted) {
    return (
      <div
        style={{
          backgroundColor: settings.backgroundColor,
          color: settings.textColor,
          padding: `${settings.padding}px 20px`,
        }}
      >
        <div className="max-w-2xl mx-auto">
          <div className="bg-green-50 border-2 border-green-500 rounded-xl p-8 text-center">
            <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-green-900 mb-2">Thank You!</h3>
            <p className="text-green-700">Your survey response has been recorded.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: settings.backgroundColor,
        color: settings.textColor,
        padding: `${settings.padding}px 20px`,
      }}
    >
      <div className="max-w-2xl mx-auto">
        {/* Progress Bar */}
        {settings.enableSections && settings.showProgressBar && totalSections > 1 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">
                Section {currentSectionIndex + 1} of {totalSections}
              </span>
              <span className="text-sm font-medium text-gray-600">
                {Math.round(((currentSectionIndex + 1) / totalSections) * 100)}%
              </span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all duration-300"
                style={{ width: `${((currentSectionIndex + 1) / totalSections) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-bold mb-3" style={{ color: settings.textColor }}>
            {settings.title}
          </h2>
          {settings.description && (
            <p className="text-base opacity-80" style={{ color: settings.textColor }}>
              {settings.description}
            </p>
          )}
        </div>

        {/* Section Header (for multi-page) */}
        {settings.enableSections && currentSection.title && (
          <div className="mb-6 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">{currentSection.title}</h3>
            {currentSection.description && (
              <p className="text-gray-600">{currentSection.description}</p>
            )}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {currentSection.questions.map((question, index) => renderQuestion(question, index))}

          {/* Navigation Buttons */}
          <div className="flex gap-4 pt-4">
            {!isFirstSection && (
              <button
                type="button"
                onClick={handlePrevious}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-all"
              >
                <ChevronLeft size={20} />
                Previous
              </button>
            )}
            <button
              type="submit"
              style={{ backgroundColor: settings.submitButtonColor }}
              className="flex-1 text-white py-4 rounded-xl font-bold text-lg hover:opacity-90 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              {isLastSection ? settings.submitButtonText : (
                <>
                  Next
                  <ChevronRight size={20} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
